import React, { useEffect, useMemo, useRef, useState } from 'react'
import { HiCheck } from 'react-icons/hi2'
import { FiSearch } from 'react-icons/fi'
import { BsThreeDots } from 'react-icons/bs'

/**
 * Quick chip selector: top N options shown as pills + a "⋯ More" popover for the rest.
 * Props:
 *  label, Icon, color ('brand'|'emerald'|'rose'|'amber'), options:[{value,label,count}],
 *  value, onChange(value), allLabel (optional => adds an "All" chip), topN (default 3), disabled
 */
export default function QuickPick({ label, Icon, color = 'brand', options = [], value, onChange, allLabel, topN = 3, disabled = false }) {
    const [open, setOpen] = useState(false)
    const [q, setQ] = useState('')
    const ref = useRef()

    useEffect(() => {
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
        document.addEventListener('click', close)
        return () => document.removeEventListener('click', close)
    }, [])

    const palette = {
        brand: { on: 'bg-brand-500 text-white shadow-brand', off: 'bg-brand-50 text-brand-700 hover:bg-brand-100', ic: 'text-brand-600' },
        emerald: { on: 'bg-emerald-500 text-white', off: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100', ic: 'text-emerald-600' },
        rose: { on: 'bg-rose-500 text-white', off: 'bg-rose-50 text-rose-700 hover:bg-rose-100', ic: 'text-rose-600' },
        amber: { on: 'bg-amber-500 text-white', off: 'bg-amber-50 text-amber-700 hover:bg-amber-100', ic: 'text-amber-600' },
    }[color] || {}

    const top = options.slice(0, topN)
    const selected = options.find(o => String(o.value) === String(value))
    const selectedInTop = selected && top.some(o => String(o.value) === String(selected.value))
    const restCount = Math.max(0, options.length - top.length)

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase()
        return s ? options.filter(o => o.label.toLowerCase().includes(s)) : options
    }, [q, options])

    const pick = (v) => { onChange(v); setOpen(false); setQ('') }

    const Chip = ({ active, onClick, children, count }) => (
        <button type="button" disabled={disabled} onClick={onClick}
            className={`chip text-[13px] px-3 py-1.5 transition active:scale-95 ${active ? palette.on : palette.off} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {children}{typeof count === 'number' && <span className={`ml-1 text-[11px] ${active ? 'text-white/80' : 'opacity-60'}`}>({count})</span>}
        </button>
    )

    return (
        <div className="w-full">
            <label className="block mb-1.5 text-sm font-medium text-gray-700 flex items-center gap-1.5">
                {Icon && <span className={palette.ic}>{Icon}</span>}{label}
            </label>
            <div className="flex flex-wrap items-center gap-2" ref={ref}>
                {allLabel !== undefined &&
                    <Chip active={!value || String(value) === '0'} onClick={() => pick(0)}>{allLabel}</Chip>}

                {top.map(o => (
                    <Chip key={o.value} active={String(o.value) === String(value)} onClick={() => pick(o.value)} count={o.count}>{o.label}</Chip>
                ))}

                {/* selected item that is outside top N -> show it so it's visible */}
                {selected && !selectedInTop &&
                    <Chip active onClick={() => { }}>{selected.label}</Chip>}

                {restCount > 0 &&
                    <div className="relative">
                        <button type="button" disabled={disabled} onClick={() => setOpen(o => !o)}
                            className={`chip text-[13px] px-3 py-1.5 bg-gray-100 text-gray-600 hover:bg-gray-200 transition ${open ? 'ring-2 ring-brand-400/40' : ''}`}>
                            <BsThreeDots className="text-base" /> {restCount} more
                        </button>
                        {open &&
                            <div className="absolute z-30 mt-2 w-64 max-w-[80vw] bg-white rounded-2xl shadow-pop ring-1 ring-gray-100 overflow-hidden animate-scale-in origin-top-left">
                                <div className="p-2 border-b border-gray-100">
                                    <div className="relative">
                                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}...`}
                                            className="w-full pl-9 pr-3 py-2 text-sm rounded-xl bg-gray-50 outline-none focus:ring-2 focus:ring-brand-400/30" />
                                    </div>
                                </div>
                                <div className="max-h-60 overflow-y-auto p-1.5">
                                    {filtered.length === 0 && <p className="px-3 py-4 text-center text-sm text-gray-400">No match</p>}
                                    {filtered.map(o => (
                                        <button key={o.value} type="button" onClick={() => pick(o.value)}
                                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left hover:bg-gray-50 ${String(o.value) === String(value) ? 'text-brand-700 font-semibold' : 'text-gray-700'}`}>
                                            <span className="flex-1 truncate">{o.label}</span>
                                            {typeof o.count === 'number' && <span className="text-xs text-gray-400">{o.count}</span>}
                                            {String(o.value) === String(value) && <HiCheck className="text-brand-500" />}
                                        </button>
                                    ))}
                                </div>
                            </div>}
                    </div>}
            </div>
        </div>
    )
}
